<template>
    <div class="w-100">
        <div v-if="loading">
            <loading/>
        </div>
        <div>
            <div v-if="!accountingFirmUserIsSet">
                <b-alert variant="warning" show>
                    Vous devez sélectionner un collaborateur pour afficher son planning
                </b-alert>
            </div>
            <div v-if="showCalendar">
                <vc-calendar
                    ref="vc"
                    class="custom-calendar w-100 shadow"
                    :masks="masks"
                    :attributes="attributes"
                    disable-page-swipe
                    is-expanded
                    @transition-end="getItems"
                >
                    <template #day-content="{ day, attributes }">
                        <div class="flex flex-col h-full z-10 overflow-hidden pointer-style"
                             title="Afficher la liste des tâches de la journée"
                             @click="handleClick(day)">
                            <span class="day-label text-sm text-gray-900">
                                <i class="fas fa-plus-square text-primary mr-2"/>{{ day.day }}
                            </span>
                            <div
                                class="flex-grow overflow-y-auto overflow-x-auto"
                            >
                                <b-link
                                    v-for="attr in attributes"
                                    :key="attr.key"
                                    title="Afficher l'échéance dans le tableau"
                                    @click="goTo(attr.customData)"
                                >
                                    <p
                                        class="vc-item leading-tight rounded-sm p-1 mb-1"
                                        :class="attr.customData.class"
                                        :style="attr.customData.style"
                                    >
                                        <i :class="attr.customData.icon"></i>
                                        <span v-if="attr.customData.type==='deadline'">
                                            {{
                                                attr.customData.client_name +
                                                ' : ' +
                                                attr.customData.action_title_full
                                            }}
                                        </span>
                                        <span
                                            v-if="attr.customData.type==='bankHoliday' || attr.customData.type==='absence'">
                                            {{
                                                attr.customData.title
                                            }}
                                        </span>
                                    </p>
                                </b-link>
                            </div>
                        </div>
                    </template>
                </vc-calendar>
            </div>
            <div v-else>
                <div class="d-flex justify-content-between align-items-center">
                    <h3 class="mb-3">Tâches planifiées le {{ dayLabel }}</h3>
                    <b-btn variant="primary" size="sm" @click="backToCalendar">
                        <i class="fas fa-fw fa-calendar mr-2"/>Vue mensuelle
                    </b-btn>
                </div>

                <table class="table">
                    <thead>
                    <tr>
                        <th>Client</th>
                        <th>Action</th>
                        <th>A faire avant</th>
                        <th class="text-right">Durée</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="task in dayTasks" :key="task.key">
                        <td>{{ task.customData.client_name }}</td>
                        <td>{{ task.customData.action_title }}</td>
                        <td>
                            {{ task.customData.deadline_date }}
                            <b-badge v-if="task.status!=='future'"
                                     class="ml-2"
                                     :style="'background-color: ' + task.customData.badge_color + ';'">
                                {{ task.customData.badge_legend }}
                            </b-badge>
                        </td>
                        <td class="text-right number-font">{{ task.duration.toFixed(2) }}&nbsp;h</td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <th colspan="3" class="text-uppercase">Total journée</th>
                        <th class="text-right number-font">{{ dayDuration.toFixed(2) }}&nbsp;h</th>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import {mapState} from 'vuex'

export default {
    name: 'PlannedTasksCalendar',

    data() {
        const month = new Date().getMonth()
        const year = new Date().getFullYear()

        return {
            displayedMonth: month,
            displayedYear: year,
            masks: {
                weekdays: 'WWW',
            },
            attributes: [],
            plannedTasks: [],
            bankHolidays: [],
            absences: [],
            meta: [],
            showCalendar: true,
            loading: false,
            dayLabel: '',
            dayTasks: [],
            dayDuration: 0,
        }
    },

    computed: {
        ...mapState({
            filters: (state) => state.reportingVM.filters,
            clientsActivationFilter: (state) => state.browsing.clientsActivationFilter

        }),
        accountingFirmUserIsSet() {
            return this.filters.accountingFirmUser > 0
        }

    },

    created() {
        this.$store.commit('browsing/SET_PAGE_NAME', 'plannedTasks')
    },

    async mounted() {
        await this.getItems()
    },

    methods: {
        goTo(customData) {
            this.$store.commit('browsing/SET_YEAR', customData.year)
            this.$store.commit('reportingVM/SET_SEARCH', customData.client_name)
            window.location.href = customData.url
        },

        async getItems() {
            if (this.accountingFirmUserIsSet) {
                try {
                    this.loading = true
                    const {year} = this.$refs.vc.pages[0]
                    const {month} = this.$refs.vc.pages[0]
                    const response = await axios.get(
                        `/reporting/plannedTasks/${year}/${month}/${this.filters.accountingFirmUser}`
                    )
                    this.plannedTasks = response.data.data.plannedTasks.data
                    this.bankHolidays = response.data.data.bankHolidays.data
                    this.absences = response.data.data.absences.data
                    this.setAttributes()
                    // this.meta = response.data.meta
                    this.loading = false
                } catch (error) {
                    console.log(error)
                    this.handleErrors(error.response)
                }
            } else {
                this.attributes = []
            }
        },

        setAttributes() {
            this.attributes = this.bankHolidays.concat(this.absences.concat(this.plannedTasks))
        },

        handleErrors(errorResponse) {
            this.loading = false
            this.errorMessage =
                "Une erreur s'est produite, les données n'ont pu être chargées. Veuillez réactualiser la page."
            this.hasErrors = true
        },

        handleClick(day) {
            this.dayLabel = day.ariaLabel
            this.dayTasks = this.attributes.filter(item => item.date === day.id)
            this.dayDuration = this.dayTasks.reduce((total, item) => total + item.duration, 0);
            this.showCalendar = false
        },

        backToCalendar() {
            this.showCalendar = true
        }
    },

}
</script>

<style scoped>
.pointer-style {
    cursor: pointer;
}
</style>
